/**
 * CP-12.4 — Qualifications-based Marketplace Search (Client UI).
 *
 * Phase 12 Build Contract r3 binding rules enforced at this surface:
 *   - Lock §32/§33 — verification layer badges (Company / Client / Self)
 *   - Lock §34 — ratings are surfaced for display only, NEVER as a filter or
 *                sort axis. UI does not expose `min_rating`, `sort_by_rating`,
 *                or "highest rated" toggles.
 *   - Lock §35 — primary vs secondary track badge on each card.
 *   - CP-12.4 visibility rule — `composite_score` MUST NEVER render on any
 *                client-facing surface (defense-in-depth grep test guards it).
 *
 * NOTE: The API may still return `composite_score` in its response payload
 * (admin diagnostic data). This component MUST NOT render or expose it. See
 * /app/backend/tests/test_cp12_4_client_ui_no_composite_score.py for the
 * binding grep test.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

function Pill({ tone = "neutral", children, testid }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
    primary: "bg-emerald-50 text-emerald-700 border-emerald-200",
    secondary: "bg-amber-50 text-amber-700 border-amber-200",
    company: "bg-indigo-50 text-indigo-700 border-indigo-200",
    client: "bg-sky-50 text-sky-700 border-sky-200",
    self: "bg-slate-50 text-slate-600 border-slate-200",
    rating: "bg-yellow-50 text-yellow-700 border-yellow-200",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[tone] || tones.neutral}`}
    >
      {children}
    </span>
  );
}

function VerificationLayerBadge({ layerRank }) {
  // layerRank comes from search SQL: 1=Company, 2=Client, 3=Self
  // CP-12.4 — we derive a coarse layer hint from `eq_score` so we never
  // display the raw composite_score. The numerical eq_score itself is
  // never rendered (defense-in-depth).
  if (layerRank === 1)
    return (
      <Pill tone="company" testid={TID.mpSearchCardVerifyBadge}>
        Company-verified
      </Pill>
    );
  if (layerRank === 2)
    return (
      <Pill tone="client" testid={TID.mpSearchCardVerifyBadge}>
        Client-verified
      </Pill>
    );
  return (
    <Pill tone="self" testid={TID.mpSearchCardVerifyBadge}>
      Self-declared
    </Pill>
  );
}

// Derive coarse best-layer hint from the API-returned eq_score:
//   * eq_score derives from (layer_rank * 4 + tier_rank) summed across required
//     equipment. For single-equipment queries, eq_score / 4 ≈ layer_rank.
//   * For multi-equipment queries we conservatively show the *worst* layer the
//     contractor has (least-strong link). This is display-only; ranking is
//     server-side.
// composite_score is NEVER referenced here — only eq_score / equipment_count.
function deriveLayerHint(row, equipmentCount) {
  if (!row || !equipmentCount) return 3;
  const avgPerEquipment = (row.eq_score || 0) / equipmentCount;
  if (avgPerEquipment <= 8) return 1; // company-verified
  if (avgPerEquipment <= 12) return 2; // client-verified
  return 3; // self-declared
}

function ContractorSearchCard({ row, equipmentCount }) {
  const layerHint = deriveLayerHint(row, equipmentCount);
  const isPrimary = row.queried_track_is_primary === true;
  return (
    <div
      data-testid={TID.mpSearchCard}
      className="avcp-card p-5 space-y-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            data-testid={TID.mpSearchCardName}
            className="text-base font-semibold text-slate-900"
          >
            {row.contractor_display_name || row.contractor_email}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {row.contractor_email}
          </div>
        </div>
        {isPrimary ? (
          <Pill tone="primary" testid={TID.mpSearchCardPrimaryBadge}>
            Primary Track
          </Pill>
        ) : (
          <Pill tone="secondary" testid={TID.mpSearchCardSecondaryBadge}>
            Secondary Track
          </Pill>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <VerificationLayerBadge layerRank={layerHint} />
        {row.rating_count > 0 ? (
          <Pill tone="rating" testid={TID.mpSearchCardRating}>
            ★ {Number(row.rating_avg).toFixed(1)} · {row.rating_count}
          </Pill>
        ) : (
          <Pill tone="neutral">No ratings yet</Pill>
        )}
      </div>
      {/*
        DO NOT render composite_score / eq_score / cert_score / layer_rank
        numeric values here. CP-12.4 client-UI lock §34 + r3 §15 step 5
        diagnostic guardrail. See backing pytest.
      */}
    </div>
  );
}

const TRACKS_FALLBACK = [
  { slug: "av_integration", label: "AV Integration" },
  { slug: "live_events", label: "Live Events" },
  { slug: "broadcast", label: "Broadcast" },
  { slug: "it_networking", label: "IT Networking" },
  { slug: "security_access", label: "Security & Access" },
];

export default function MarketplaceSearch() {
  const { user } = useAuth();
  const [tracks, setTracks] = useState(TRACKS_FALLBACK);
  const [roles, setRoles] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [certs, setCerts] = useState([]);

  const [filters, setFilters] = useState({
    track: "live_events",
    role: "",
    equipment: [],
    certification: [],
    country: "",
  });
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  // Hydrate catalogs (best-effort; we ship fallbacks)
  useEffect(() => {
    (async () => {
      try {
        const [t, r, e, c] = await Promise.all([
          api("/catalogs/tracks").catch(() => ({ data: [] })),
          api("/catalogs/roles").catch(() => ({ data: [] })),
          api("/catalogs/equipment-tags").catch(() => ({ data: [] })),
          api("/catalogs/qualification-certifications").catch(() => ({ data: [] })),
        ]);
        if (t.data?.length) setTracks(t.data);
        setRoles(r.data || []);
        setEquipment(e.data || []);
        setCerts(c.data || []);
      } catch (err) {
        // ignore — fallback shown
      }
    })();
  }, []);

  const equipmentForTrack = useMemo(() => {
    if (!equipment?.length) return [];
    // Show all equipment for now; backend filters by slug regardless of track
    return equipment;
  }, [equipment]);

  const rolesForTrack = useMemo(() => {
    if (!roles?.length) return [];
    return roles.filter((r) =>
      r.track_slug ? r.track_slug === filters.track : true
    );
  }, [roles, filters.track]);

  async function runSearch(e) {
    e?.preventDefault?.();
    if (!filters.track || !filters.role || filters.equipment.length === 0) {
      toast.error(
        "Track, role, and at least one equipment item are required (Lock §27 + §28)"
      );
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("track", filters.track);
      params.set("role", filters.role);
      params.set("equipment", filters.equipment.join(","));
      if (filters.certification.length)
        params.set("certification", filters.certification.join(","));
      if (filters.country) params.set("country", filters.country);
      const body = await api(`/marketplace/search?${params.toString()}`);
      setResults(body.data || []);
      setSearched(true);
    } catch (err) {
      toast.error(err.message || "Search failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <div
        data-testid={TID.mpSearchPage}
        className="space-y-6 max-w-6xl mx-auto px-4 py-8"
      >
        <header>
          <h1 className="text-2xl font-semibold">Find Workforce Partners</h1>
          <p className="text-sm text-slate-500 mt-1">
            Qualifications-first search. Ranking favors verified experience over
            self-declared claims. Ratings are display only.
          </p>
        </header>

        <form
          onSubmit={runSearch}
          className="avcp-card p-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        >
          <label className="grid gap-1 text-sm">
            <span className="font-medium">Industry Track</span>
            <select
              data-testid={TID.mpSearchTrackSelect}
              className="avcp-input"
              value={filters.track}
              onChange={(e) =>
                setFilters({ ...filters, track: e.target.value, role: "" })
              }
            >
              {tracks.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.label || t.slug}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1 text-sm">
            <span className="font-medium">Role</span>
            <select
              data-testid={TID.mpSearchRoleSelect}
              className="avcp-input"
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
            >
              <option value="">— Select —</option>
              {rolesForTrack.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.label || r.slug}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1 text-sm">
            <span className="font-medium">Country (work-auth filter)</span>
            <select
              data-testid={TID.mpSearchCountrySelect}
              className="avcp-input"
              value={filters.country}
              onChange={(e) =>
                setFilters({ ...filters, country: e.target.value })
              }
            >
              <option value="">Any</option>
              <option value="CA">Canada</option>
              <option value="US">United States</option>
              <option value="UK">United Kingdom</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="NL">Netherlands</option>
              <option value="AU">Australia</option>
            </select>
          </label>

          <label className="grid gap-1 text-sm md:col-span-2 lg:col-span-2">
            <span className="font-medium">Equipment (required, multi-select)</span>
            <select
              data-testid={TID.mpSearchEquipmentSelect}
              className="avcp-input h-32"
              multiple
              value={filters.equipment}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  equipment: Array.from(e.target.selectedOptions).map(
                    (o) => o.value
                  ),
                })
              }
            >
              {equipmentForTrack.map((eq) => (
                <option key={eq.slug} value={eq.slug}>
                  {eq.label || eq.slug}
                </option>
              ))}
            </select>
            <span className="text-xs text-slate-500">
              Hold Ctrl/Cmd to select multiple. At least one is required.
            </span>
          </label>

          <label className="grid gap-1 text-sm">
            <span className="font-medium">Certifications (optional)</span>
            <select
              data-testid={TID.mpSearchCertSelect}
              className="avcp-input h-32"
              multiple
              value={filters.certification}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  certification: Array.from(e.target.selectedOptions).map(
                    (o) => o.value
                  ),
                })
              }
            >
              {certs.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.label || c.slug}
                </option>
              ))}
            </select>
          </label>

          <div className="md:col-span-2 lg:col-span-3 flex justify-end">
            <button
              type="submit"
              data-testid={TID.mpSearchSubmit}
              className="avcp-btn-primary"
              disabled={loading}
            >
              {loading ? "Searching…" : "Search"}
            </button>
          </div>
        </form>

        {searched && (
          <div data-testid={TID.mpSearchResults} className="space-y-3">
            <div className="text-sm text-slate-500">
              {results.length} result{results.length === 1 ? "" : "s"} ·
              ranking: Track → Equipment → Verification layer → Rating
              (tie-break only)
            </div>
            {results.length === 0 ? (
              <div
                data-testid={TID.mpSearchEmpty}
                className="avcp-card p-8 text-center text-slate-500"
              >
                No contractors matched these qualifications. Try broadening
                equipment or removing the country filter.
              </div>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {results.map((row) => (
                  <ContractorSearchCard
                    key={row.contractor_user_id}
                    row={row}
                    equipmentCount={filters.equipment.length}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Contractor-side Location + Travel forms (Lock §21 + §30)
// Exported as named components so the existing ContractorQualification wizard
// can mount them as new steps without re-architecting the wizard shell.
// ---------------------------------------------------------------------------

const TRAVEL_RADII = ["local", "regional", "national", "international"];
const COMMON_COUNTRIES = ["CA", "US", "UK", "DE", "FR", "NL", "AU", "MX", "BR", "JP"];

export function ContractorLocationForm() {
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [city, setCity] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/me/contractor-profile/location");
        if (r) {
          setCountry(r.country_code || "");
          setRegion(r.region || "");
          setCity(r.city || "");
        }
      } catch {
        /* 404 = not set yet */
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  async function save(e) {
    e.preventDefault();
    if (!country || !region || !city) {
      toast.error("Country, region, and city are required.");
      return;
    }
    setSaving(true);
    try {
      await api("/me/contractor-profile/location", {
        method: "PUT",
        body: { country_code: country.toUpperCase(), region, city },
      });
      toast.success("Location saved.");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) return null;

  return (
    <form
      onSubmit={save}
      data-testid={TID.qualLocationForm}
      className="avcp-card p-5 space-y-4"
    >
      <div>
        <h3 className="font-semibold">Base Location</h3>
        <p className="text-xs text-slate-500">
          Contractor-controlled. Used for travel-radius scoring (Lock §21).
        </p>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="grid gap-1 text-sm">
          <span>Country (ISO-2)</span>
          <select
            data-testid={TID.qualLocationCountry}
            className="avcp-input"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          >
            <option value="">— Select —</option>
            {COMMON_COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          <span>Region / Province / State</span>
          <input
            data-testid={TID.qualLocationRegion}
            className="avcp-input"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="ON"
          />
        </label>
        <label className="grid gap-1 text-sm">
          <span>City</span>
          <input
            data-testid={TID.qualLocationCity}
            className="avcp-input"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Toronto"
          />
        </label>
      </div>
      <div className="flex justify-end">
        <button
          type="submit"
          data-testid={TID.qualLocationSave}
          className="avcp-btn-primary"
          disabled={saving}
        >
          {saving ? "Saving…" : "Save location"}
        </button>
      </div>
    </form>
  );
}

export function ContractorTravelForm() {
  const [radius, setRadius] = useState("regional");
  const [workAuth, setWorkAuth] = useState([]);
  const [passport, setPassport] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/me/contractor-profile/travel");
        if (r) {
          setRadius(r.travel_radius || "regional");
          setWorkAuth(r.work_auth_countries || []);
          setPassport(!!r.has_passport);
        }
      } catch {
        /* 404 — not set yet */
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  function toggleCountry(code) {
    setWorkAuth((cur) =>
      cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]
    );
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api("/me/contractor-profile/travel", {
        method: "PUT",
        body: {
          travel_radius: radius,
          work_auth_countries: workAuth.map((c) => c.toUpperCase()),
          has_passport: passport,
        },
      });
      toast.success("Travel saved.");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) return null;

  return (
    <form
      onSubmit={save}
      data-testid={TID.qualTravelForm}
      className="avcp-card p-5 space-y-4"
    >
      <div>
        <h3 className="font-semibold">Travel & Work Authorization</h3>
        <p className="text-xs text-slate-500">
          Lock §30 — search hard-filters by country of work authorization.
        </p>
      </div>

      <label className="grid gap-1 text-sm">
        <span>Travel radius</span>
        <select
          data-testid={TID.qualTravelRadius}
          className="avcp-input"
          value={radius}
          onChange={(e) => setRadius(e.target.value)}
        >
          {TRAVEL_RADII.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </label>

      <div data-testid={TID.qualTravelWorkAuth}>
        <div className="text-sm font-medium mb-1">
          Work-auth countries (ISO-2)
        </div>
        <div className="flex flex-wrap gap-2">
          {COMMON_COUNTRIES.map((c) => {
            const on = workAuth.includes(c);
            return (
              <button
                key={c}
                type="button"
                onClick={() => toggleCountry(c)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                  on
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                }`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-testid={TID.qualTravelPassport}
          checked={passport}
          onChange={(e) => setPassport(e.target.checked)}
        />
        <span>Has valid passport (enables international travel rank boost)</span>
      </label>

      <div className="flex justify-end">
        <button
          type="submit"
          data-testid={TID.qualTravelSave}
          className="avcp-btn-primary"
          disabled={saving}
        >
          {saving ? "Saving…" : "Save travel"}
        </button>
      </div>
    </form>
  );
}
